import { PlusOutlineIcon } from '@carboncopyco/icons';
import { Button, TextCaption, Tooltip, TooltipMessage } from '@carboncopyco/ui';
import { useBoolean } from 'ahooks';
import classNames from 'classnames';
import { isEqual } from 'lodash-es';
import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { RulesContent, RulesContentCreationForm } from '../../RulesManager';
import type { DynamicResponseModifier } from './CreateDynamicResponse';
import { DynamicResponseModifierForm } from './DynamicResponseModiferForm';
import { DynamicResponseModifiersListItem } from './DynamicResponseModifiersListItem';

const MAX_MODIFIERS = 10;

export const DynamicResponseModifiersList = () => {
  const { register, setValue, watch, trigger, resetField, formState } =
    useFormContext();
  const [isFormOpen, setIsFormOpen] = useBoolean(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const defaultModifiers = formState.defaultValues.dynamicResponseModifiers;
  const modifiers = watch('dynamicResponseModifiers');
  const reachedModifiersLimit = modifiers.length >= MAX_MODIFIERS;

  const isModifiersChanged = (newModifers: DynamicResponseModifier[]) => {
    return !isEqual(
      newModifers.map((m) => ({
        ...m.arguments,
      })),
      defaultModifiers.map((m) => ({ ...m.arguments }))
    );
  };

  const onChangeHandler = (newModifers: DynamicResponseModifier[]) => {
    setValue('dynamicResponseModifiers', newModifers, {
      shouldDirty: true,
    });
    trigger('dynamicResponseModifiers');

    //Reset the dirty state if the modifers are the same as the default ones.
    if (!isModifiersChanged(newModifers)) {
      resetField('dynamicResponseModifiers', {
        keepDirty: false,
      });
    }
  };

  useEffect(() => {
    register('dynamicResponseModifiers');
  }, []);

  return (
    <RulesContent>
      <div className="h-full flex w-full overflow-x-hidden overflow-auto">
        <div className="flex flex-col w-full gap-2.5 p-2.5 box-border">
          <RulesContentCreationForm show={isFormOpen || !modifiers.length}>
            <DynamicResponseModifierForm
              onSubmit={(modifier) => {
                onChangeHandler([...modifiers, modifier]);
                setIsFormOpen.setFalse();
              }}
              onCancel={setIsFormOpen.setFalse}
            />
          </RulesContentCreationForm>
          <div className="flex flex-col flex-1 overflow-auto">
            <RulesContent.List<DynamicResponseModifier>
              rules={modifiers.map((modifier) => ({
                id: modifier.id,
                rule: modifier,
              }))}
              renderItem={({ ruleItem, open, openRule, closeRule }) => (
                <DynamicResponseModifiersListItem
                  isOpen={open}
                  onCancel={closeRule}
                  openRule={openRule}
                  modifier={ruleItem.rule}
                  onDelete={({ id }) => {
                    onChangeHandler(modifiers.filter((m) => m.id !== id));
                  }}
                  onEdit={(modifier) => {
                    onChangeHandler(
                      modifiers.map((m) =>
                        m.id === modifier.id ? modifier : m
                      )
                    );
                    closeRule();
                  }}
                />
              )}
            />
          </div>

          <div
            className={classNames(
              'flex w-full p-2.5 rounded-md outline-dashed outline-1 outline-ds-natural-150 cursor-pointer',
              reachedModifiersLimit && 'bg-ds-natural-50'
            )}
            onClick={() => {
              if (!reachedModifiersLimit) {
                setIsFormOpen.setTrue();
              }
            }}
          >
            <div
              className="py-ds-p-7"
              ref={anchorRef}
            >
              <Button
                link
                blue
                disabled={reachedModifiersLimit}
                leftIcon={<PlusOutlineIcon size={20} />}
                label="Add condition"
                noPadding
              />
            </div>
            {reachedModifiersLimit && (
              <Tooltip
                trigger="mouseenter"
                anchorRef={anchorRef}
                placement="bottom-start"
                offset={[0, -2]}
                shadow="lg"
                autoCalcArrowPosition
              >
                <TooltipMessage
                  title={
                    <span className="flex gap-1 justify-center items-center">
                      <TextCaption
                        label={`You have reached the limit of ${MAX_MODIFIERS} conditions.`}
                        color="text-ds-natural-600"
                      />
                    </span>
                  }
                />
              </Tooltip>
            )}
          </div>
        </div>
      </div>
    </RulesContent>
  );
};
